import { useCallback } from 'react';
import { DropEvent, FileRejection } from 'react-dropzone';
import Markdown from 'shared/components/Markdown';

import Spacer from 'design-system/components/Spacer';
import Button, { ButtonType } from 'inquiry/components/Button';
import Dialog from 'inquiry/components/Dialog';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { useStepDocumentStyleContext } from 'inquiry/context/StepDocumentStyleContext';
import DocumentsPrompt from 'inquiry/views/InquiryDocumentsPromptView/DocumentsPrompt';
import DocumentsReview from 'inquiry/views/InquiryDocumentsPromptView/DocumentsReview';
import { StepDocumentConfig } from 'lib/interfaces/StepsConfig';
import { FullScreenLoadingSpinner } from 'verify/components/LoadingSpinner';
import {
  DocumentCaptureOptionDesktop,
  DocumentCaptureOptionMobile,
} from 'verify/lib/interfaces/CaptureOption';
import PromptOptions from 'verify/lib/interfaces/PromptOptions';
import { DocumentFile } from 'verify/models/document-file';
import { DocumentUpload } from 'verify/redux/reducers/document';

interface InquiryDocumentsPromptViewProps {
  allowedCaptureOptions: Array<DocumentCaptureOptionDesktop | DocumentCaptureOptionMobile>;
  acceptedFileTypes: StepDocumentConfig['accepted-file-types'];
  assets: StepDocumentConfig['assets']['prompt-page'];
  documentFiles: DocumentFile[];
  documentFileLimit: number;
  handleAddFiles: (files: File[]) => Promise<void>;
  handleDeviceHandoff: () => void;
  handleDrop: (acceptedFiles: File[], rejectedFiles: FileRejection[], event: DropEvent) => void;
  handleDropRejected: (rejectedFiles: FileRejection[]) => void;
  handleRemoveFile: (fileId: string) => Promise<void>;
  handleInputCapture: (file: File) => void;
  handleSelectCamera: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  hideCameraPermissionsDialog: () => void;
  isProcessing: PromptOptions | undefined;
  loading: boolean;
  localizations: StepDocumentConfig['localizations']['prompt-page'];
  openCameraPermissionsDialog: () => void;
  page: StepDocumentConfig['start-page'];
  showCameraPermissionsDialog: boolean;
  submitting: boolean;
  uploads: DocumentUpload[];
  pendingDeletions: string[];
  multiFileSupported?: boolean;
  /**
   * Indicates if the prompt view is being skipped due to webcam being the only
   * capture option. We use this flag because allowedCaptureOptions may still be
   * empty based on browser permissions.
   */
  skipPromptPage: boolean;
}

export default function InquiryDocumentsPromptView({
  allowedCaptureOptions,
  acceptedFileTypes,
  assets,
  documentFiles,
  documentFileLimit,
  handleAddFiles,
  handleDeviceHandoff,
  handleDrop,
  handleDropRejected,
  handleInputCapture,
  handleRemoveFile,
  handleSelectCamera: onSelectCamera,
  handleSubmit,
  hideCameraPermissionsDialog,
  isProcessing,
  loading,
  localizations,
  openCameraPermissionsDialog,
  page,
  showCameraPermissionsDialog,
  submitting,
  uploads,
  pendingDeletions,
  multiFileSupported,
  skipPromptPage,
}: InquiryDocumentsPromptViewProps) {
  const { base, buttons: buttonStyles } = useStepDocumentStyleContext();

  const handleSelectCamera = useCallback(async () => {
    try {
      await onSelectCamera();
    } catch (e) {
      openCameraPermissionsDialog();
    }
  }, []);

  // This only happens when the only capture option is web, skip prompt page is
  // configured, and user hasn't enabled camera permissions yet
  if (skipPromptPage && allowedCaptureOptions.length === 0) {
    return <FullScreenLoadingSpinner />;
  }

  // Also need client side logic to check files length because step does not
  // update when adding images to document
  const showReviewPage =
    page === 'review' || (multiFileSupported && documentFiles.length + uploads.length > 0);

  return (
    <>
      <Dialog
        isOpen={showCameraPermissionsDialog}
        onClose={hideCameraPermissionsDialog}
        styles={base?.modal}
        data-test='camera-permissions-dialog'
        aria-labelledby='camera-permissions-dialog__title'
        aria-describedby='camera-permissions-dialog__description'
      >
        <ThemedTitle id='camera-permissions-dialog__title' styles={base?.titleStyle}>
          <Markdown>{localizations['camera-permissions-title']}</Markdown>
        </ThemedTitle>
        <ThemedParagraph id='camera-permissions-dialog__description' styles={base?.textStyle}>
          <Markdown>{localizations['camera-permissions-prompt']}</Markdown>
        </ThemedParagraph>
        <Spacer height={2} />
        <Button
          type='button'
          id='camera-permissions-dialog__btn'
          styles={buttonStyles?.[ButtonType.Primary]}
          onClick={hideCameraPermissionsDialog}
        >
          {localizations['camera-permissions-btn-continue-web']}
        </Button>
      </Dialog>

      {showReviewPage ? (
        <DocumentsReview
          assets={assets}
          allowedCaptureOptions={allowedCaptureOptions}
          acceptedFileTypes={acceptedFileTypes}
          documentFiles={documentFiles}
          documentFileLimit={documentFileLimit}
          handleAddError={handleDropRejected}
          handleAddFiles={handleAddFiles}
          handleDeviceHandoff={handleDeviceHandoff}
          handleRemoveFile={handleRemoveFile}
          handleSelectCamera={handleSelectCamera}
          handleSubmit={handleSubmit}
          loading={loading}
          localizations={localizations}
          submitting={submitting}
          uploads={uploads}
          pendingDeletions={pendingDeletions}
        />
      ) : (
        <DocumentsPrompt
          allowedCaptureOptions={allowedCaptureOptions}
          acceptedFileTypes={acceptedFileTypes}
          assets={assets}
          handleDeviceHandoff={handleDeviceHandoff}
          handleDrop={handleDrop}
          handleDropRejected={handleDropRejected}
          handleInputCapture={handleInputCapture}
          handleSelectCamera={handleSelectCamera}
          isProcessing={isProcessing}
          loading={loading}
          localizations={localizations}
          multiFileSupported={multiFileSupported}
        />
      )}
    </>
  );
}
